import {
  PackagePlus,
  AlertTriangle,
  ArrowDownToLine,
  ArrowDownUp,
  BarChart3,
  Boxes,
  Contact,
  DatabaseBackup,
  FileCog,
  FolderTree,
  Grid3x3,
  KeyRound,
  Landmark,
  LayoutDashboard,
  Package,
  RadioTower,
  Rocket,
  Receipt,
  ScrollText,
  ShieldCheck,
  SlidersHorizontal,
  UserRound,
  Users,
  Video,
  type LucideIcon,
} from 'lucide-react';
import type { AppUser } from '@/lib/api';

/**
 * Nawigacja portalu — JEDNO źródło dla menu ORAZ dla dostępu do trasy.
 *
 * Wyjęte z `AppShell`, bo lista pozycji i lista `<Route>` w `App.tsx` mówiły to samo dwa razy:
 * menu ukrywało pozycję, a wpisanie adresu z palca renderowało ekran mimo wszystko. Przy koncie
 * klienta przestaje to być kosmetyką — bez tego admin klienta wchodził na `/edytor`, czyli
 * na najgroźniejszą powierzchnię w portalu.
 *
 * `ready: false` = moduł jeszcze nie istnieje (Faza 4).
 * `module` = klucz uprawnienia; sprawdzany wobec listy EFEKTYWNEJ z serwera, nie wobec roli.
 * `adminOnly` / `sdiOnly` = dodatkowe warunki, sprawdzane NIEZALEŻNIE od modułu.
 *
 * ⚠️ „Konta portalu" (`/konta`) to konta LOGOWANIA do portalu (`AppUser`).
 *    „Pracownicy" (`/pracownicy`) to pracownicy SPS pobierający towar z automatów — INNA encja.
 */
/**
 * SEKCJE MENU — układ przeniesiony ze starego Vendi (odczytany z jego paska bocznego 2026-08-10:
 * „LUDZIE", „ASORTYMENT", „ADMINISTRACJA", a Pulpit i Automaty bez nagłówka).
 *
 * ⚠️ SEKCJA JEST CZĘŚCIĄ POZYCJI, nie osobną tablicą obok. Druga lista mówiąca „ta pozycja należy
 * tam" rozjechałaby się przy pierwszym dodanym ekranie, a objaw byłby cichy: pozycja znika
 * z menu albo ląduje w sekcji, w której nikt jej nie szuka.
 */
export type Sekcja = 'przeglad' | 'ludzie' | 'asortyment' | 'administracja';

/** Kolejność sekcji i klucz nagłówka. `przeglad` bez nagłówka — jak w starym Vendi. */
export const SEKCJE: readonly { id: Sekcja; key: string | null }[] = [
  { id: 'przeglad', key: null },
  { id: 'ludzie', key: 'nav.section.people' },
  { id: 'asortyment', key: 'nav.section.stock' },
  { id: 'administracja', key: 'nav.section.admin' },
];

export type NavItem = {
  to: string;
  key: string;
  icon: LucideIcon;
  ready: boolean;
  module: string | null;
  adminOnly: boolean;
  sdiOnly: boolean;
  sekcja: Sekcja;
};

export const NAV: readonly NavItem[] = [
  { to: '/', key: 'nav.dashboard', icon: LayoutDashboard, ready: true, module: null, adminOnly: false, sdiOnly: false , sekcja: 'przeglad' },
  { to: '/automaty', key: 'nav.machines', icon: Boxes, ready: true, module: 'machines', adminOnly: false, sdiOnly: false , sekcja: 'przeglad' },
  { to: '/produkty', key: 'nav.items', icon: Package, ready: true, module: 'items', adminOnly: false, sdiOnly: false , sekcja: 'asortyment' },
  // grupy TOWAROWE — pod tym samym uprawnieniem co towary: kto widzi towar, widzi jego grupę.
  // Nie mylić z `/grupy`, gdzie są grupy PRACOWNICZE.
  { to: '/grupy-towarowe', key: 'nav.itemGroups', icon: FolderTree, ready: true, module: 'items', adminOnly: false, sdiOnly: false , sekcja: 'asortyment' },
  { to: '/planogram', key: 'nav.planogram', icon: Grid3x3, ready: true, module: 'planogram', adminOnly: false, sdiOnly: false , sekcja: 'asortyment' },
  { to: '/transakcje', key: 'nav.transactions', icon: Receipt, ready: true, module: 'transactions', adminOnly: false, sdiOnly: false , sekcja: 'asortyment' },
  /**
   * NAGRANIA Z KAMERY SPS (06.10.2026) — wrzutnia u MJM/Gestamp.
   *
   * ⚠️ TYLKO ADMIN: nagranie pokazuje twarz i ręce pracownika. Szerszy dostęp przyjdzie z API dla
   * Vending+ (osobny zakres tokenu), a nie przez otwarcie tej pozycji wszystkim z modułem transakcji.
   */
  { to: '/nagrania', key: 'nav.recordings', icon: Video, ready: true, module: 'transactions', adminOnly: true, sdiOnly: false , sekcja: 'asortyment' },
  /**
   * RAPORTOWANIE — pozycja zamówiona 2026-08-08, ekran istnieje od 2026-08-13.
   *
   * Pierwszy raport to STAN AUTOMATÓW („co dowieźć"): różnica do pełna per automat plus
   * podsumowanie do magazynu. Drugi zamówiony raport — wydania za okres — mieszka na ekranie
   * Transakcji, bo tam są już wszystkie filtry i eksporty.
   */
  { to: '/raportowanie', key: 'nav.reports', icon: BarChart3, ready: true, module: 'reports', adminOnly: false, sdiOnly: false , sekcja: 'asortyment' },
  /**
   * ⚠️ IKONY „PRACOWNICY" I „GRUPY" NIOSĄ ROZRÓŻNIENIE: jeden człowiek kontra wielu.
   *
   * Zgłoszone 2026-08-10 („Grupy — zmienić ikonę, żeby było wiele ludzików"): Grupy miały
   * `UsersRound`, które w tym rozmiarze czyta się jak pojedynczą postać, a Pracownicy trzymali
   * `Users`, czyli tę z tłumem. Rozróżnienie było więc odwrócone względem znaczenia ekranów.
   */
  { to: '/pracownicy', key: 'nav.employees', icon: UserRound, ready: true, module: 'employees', adminOnly: false, sdiOnly: false , sekcja: 'ludzie' },
  { to: '/grupy', key: 'nav.groups', icon: Users, ready: true, module: 'groups', adminOnly: false, sdiOnly: false , sekcja: 'ludzie' },
  { to: '/przypisania', key: 'nav.assignments', icon: ArrowDownUp, ready: true, module: 'assignments', adminOnly: false, sdiOnly: false , sekcja: 'ludzie' },
  /**
   * CENTRA KOSZTOWE — pod uprawnieniem `employees`, bo to informacja o LUDZIACH: centrum jest
   * atrybutem pracownika w SPS (`Users.COSTCENTER` + przynależność), a nie osobnym słownikiem
   * do zarządzania w oderwaniu od kadry. Kto nie widzi pracowników, nie ma tu czego szukać.
   */
  { to: '/centra-kosztowe', key: 'nav.costCenters', icon: Landmark, ready: true, module: 'employees', adminOnly: false, sdiOnly: false , sekcja: 'ludzie' },
  /**
   * HR+ — połączenie z kadrami klienta (06.10.2026). Za `employees` i TYLKO ADMIN: ekran trzyma klucz do
   * obcego systemu, a podgląd zestawia całą kadrę z HR+.
   */
  { to: '/hr-plus', key: 'nav.hrplus', icon: Contact, ready: true, module: 'employees', adminOnly: true, sdiOnly: false , sekcja: 'ludzie' },
  /**
   * UPRAWNIENIA — dostęp do produktów (`Restrictions2`) razem z limitem (`Rationing`).
   *
   * ⚠️ MODUŁ `limits`, BO TA ZAKŁADKA ZASTĄPIŁA „OGRANICZENIA" (28.08.2026). Stały obok siebie
   * do czasu, aż Uprawnienia pokażą wszystko, co pokazywały Ograniczenia — pokazują, więc
   * Ograniczenia zniknęły na wprost zgłoszone „mamy to samo w Uprawnieniach i zupełnie nie
   * będzie używane". Nazwa modułu zostaje TA SAMA: zmiana odebrałaby ekran każdemu, kto ma
   * dziś `limits` w uprawnieniach, a chodziło o skasowanie duplikatu, nie o zmianę dostępu.
   */
  { to: '/uprawnienia', key: 'nav.permissions', icon: KeyRound, ready: true, module: 'limits', adminOnly: false, sdiOnly: false , sekcja: 'asortyment' },
  // monitor kolejki = tylko do czytania, więc wystarczy uprawnienie punktowe.
  // Ikona „w dół do kreski", bo to jest właśnie config-down: portal → automaty. `ArrowDownUp`
  // stało tu wcześniej i dublowało ikonę Przypisań — w zwiniętym menu obie pozycje wyglądały
  // identycznie, a ikona jest tam jedynym znakiem rozpoznawczym.
  { to: '/zmiany', key: 'nav.changes', icon: ArrowDownToLine, ready: true, module: 'changes', adminOnly: false, sdiOnly: false , sekcja: 'administracja' },
  /**
   * Edytor config-down = najgroźniejsza powierzchnia w portalu: rola admina ORAZ uprawnienie
   * `changes`. Do 2026-08-03 stało tu `module: null`, więc pozycja wystarczała się roli — a admin
   * klienta jest adminem i widział ją u siebie. API wymaga jednego i drugiego
   * (`@RequireModules('changes')` + `AdminGuard`), więc menu musi wymagać tego samego.
   */
  { to: '/edytor', key: 'nav.authoring', icon: FileCog, ready: true, module: 'changes', adminOnly: true, sdiOnly: false , sekcja: 'administracja' },
  { to: '/alerty', key: 'nav.alerts', icon: AlertTriangle, ready: true, module: 'alerts', adminOnly: false, sdiOnly: false , sekcja: 'przeglad' },
  /**
   * Agenci = POTWIERDZANIE węzłów, czyli otwieranie zapisu do read-modelu. Trzy warunki naraz,
   * dokładnie te, których wymaga API (`@RequireModules('agents')` + `AdminGuard` + `SdiOnlyGuard`).
   * Menu musi wymagać tego samego co trasa — inaczej pozycja jest widoczna i kończy się odmową.
   */
  { to: '/agenci', key: 'nav.agents', icon: RadioTower, ready: true, module: 'agents', adminOnly: true, sdiOnly: true , sekcja: 'administracja' },
  /**
   * KREATOR WDROŻENIA SMART SYNCA — te same trzy warunki co Agenci, i z tego samego powodu:
   * prowadzi przez wydanie kodu rejestracyjnego, przełączenie trybu wyspy, wydanie tokenu,
   * odtworzenie bazy i włączenie rozsyłania wydań. Ekran zbierający te czynności w jedno nie może
   * być dostępny szerzej niż najwęższa z nich — inaczej byłby ich obejściem.
   */
  { to: '/smart-sync', key: 'nav.smartSync', icon: Rocket, ready: true, module: 'agents', adminOnly: true, sdiOnly: true , sekcja: 'administracja' },
  /**
   * ⚠️ `sdiOnly`, tak jak agenci i tokeny. To zdalne uruchamianie kodu na maszynie klienta —
   * robota wdrożeniowa, nie samoobsługa, nawet dla admina po stronie klienta.
   */
  { to: '/programy', key: 'nav.programy', icon: PackagePlus, ready: true, module: 'machines', adminOnly: true, sdiOnly: true , sekcja: 'administracja' },
  /**
   * Kopie zapasowe bazy SPS. Te same trzy warunki co Agenci, bo zlecenie kopii wymaga listy
   * agentów (`/agents` jest SDI-only) — a bez niej nie ma czym wskazać maszyny do skopiowania.
   * Sam PODGLĄD kopii API dopuszcza szerzej (`machines` + admin); rozszerzenie dostępu na
   * admina klienta to osobna decyzja, nie efekt uboczny przeniesienia przycisku.
   */
  { to: '/kopie', key: 'nav.backups', icon: DatabaseBackup, ready: true, module: 'machines', adminOnly: true, sdiOnly: true , sekcja: 'administracja' },
  // logi = ROLA SERWISOWA. Osobne uprawnienie od `changes`: rejestr zmian pokazuje, co portal
  // zamierza zrobić z bazą klienta, a logi — co dzieje się w portalu i kto tam działał.
  { to: '/logi', key: 'nav.service', icon: ScrollText, ready: true, module: 'service', adminOnly: false, sdiOnly: true , sekcja: 'administracja' },
  // branding instancji: admin KLIENTA też ma tu wstęp — jedna instancja to jeden klient (§8a),
  // a wymóg „samoobsługowo" znaczy, że klient nie musi prosić nas o zmianę logo
  { to: '/ustawienia', key: 'nav.settings', icon: SlidersHorizontal, ready: true, module: null, adminOnly: true, sdiOnly: false , sekcja: 'administracja' },
  /**
   * ⚠️ Admin KLIENTA wciąż tu wchodzi i może zakładać konta u siebie — a §8a mówi „świadomie
   * bez zakładania użytkowników". Zostawione ZGODNIE ze stanem API (`/accounts` jest za samym
   * `AdminGuard`, bez modułu, więc sufit klienta go nie dotyczy). Decyzja produktowa: kto
   * onboarduje ludzi klienta. Nie ukrywam pozycji, której API nie blokuje — menu kłamiące
   * o uprawnieniach jest gorsze niż widoczna niespójność.
   */
  { to: '/konta', key: 'nav.accounts', icon: ShieldCheck, ready: true, module: null, adminOnly: true, sdiOnly: false , sekcja: 'administracja' },
] as const;

/**
 * Czy `user` może zobaczyć tę pozycję i wejść na jej trasę.
 *
 * Trzy warunki sprawdzane NIEZALEŻNIE. Poprzednia wersja miała `adminOnly ? isAdmin : moduł`,
 * czyli pozycja dla admina IGNOROWAŁA moduł — i tak wpadł `/edytor` do menu admina klienta.
 */
export function canSee(user: AppUser | null, item: NavItem): boolean {
  if (!user) return false;
  if (item.sdiOnly && user.clientId !== null) return false;
  if (item.adminOnly && user.role !== 'ADMIN') return false;
  if (item.module !== null && !user.effectiveModules.includes(item.module)) return false;
  return true;
}

export const navFor = (user: AppUser | null): NavItem[] => NAV.filter((item) => canSee(user, item));

/**
 * Menu POGRUPOWANE — sekcje w stałej kolejności, PUSTE POMINIĘTE.
 *
 * ⚠️ Pominięcie pustej sekcji jest tu warunkiem poprawności, nie estetyką: konto klienta bez
 * uprawnień administracyjnych widziałoby inaczej goły nagłówek „ADMINISTRACJA" bez ani jednej
 * pozycji — czyli menu obiecywałoby ekrany, do których ten użytkownik nie ma wstępu.
 */
export function navSekcjeFor(user: AppUser | null): { id: Sekcja; key: string | null; items: NavItem[] }[] {
  const widoczne = navFor(user);
  return SEKCJE.map((s) => ({ ...s, items: widoczne.filter((i) => i.sekcja === s.id) })).filter(
    (s) => s.items.length > 0,
  );
}

/** Wymagania trasy albo `undefined`, gdy adresu nie ma w nawigacji. */
export const navItemFor = (path: string): NavItem | undefined => NAV.find((item) => item.to === path);
